/**
 * AccountPage — toate setările care țin de user-ul logat (nu de tenant):
 *   - Profile (email, user id, tenant curent)
 *   - Aparență (temă + limbă)
 *   - Securitate (2FA, parolă master, sesiuni)
 *   - Invitații tenant (doar owner/admin)
 */
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import ChangePasswordSection from '@/features/settings/ChangePasswordSection';
import RecoveryPhraseSection from '@/features/settings/RecoveryPhraseSection';
import SessionsSection from '@/features/settings/SessionsSection';
import TwoFactorSection from '@/features/settings/TwoFactorSection';
import DeleteSpaceSection from '@/features/account/DeleteSpaceSection';
import InvitesSection from '@/features/account/InvitesSection';
import MembersSection from '@/features/account/MembersSection';
import LanguagePicker from '@/features/account/LanguagePicker';
import ThemeToggle from '@/features/account/ThemeToggle';

export default function AccountPage() {
  const { t } = useTranslation();
  const { user, currentTenantId } = useAuthStore();
  const currentTenant = user?.tenants.find((x) => x.tenant_id === currentTenantId);
  const canInvite =
    currentTenant?.role === 'owner' || currentTenant?.role === 'admin';

  return (
    <div>
      <div className="mb-6">
        <h1 className="display-title">{t('account.title')}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t('account.subtitle')}
        </p>
      </div>

      <div className="space-y-4">
        {/* ========== Profile ========== */}
        <div className="card">
          <h2 className="display-title-sm mb-3">{t('settings.profile')}</h2>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between border-b border-[var(--border)] pb-2">
              <dt className="text-[var(--text-muted)]">{t('auth.email')}</dt>
              <dd className="font-mono text-[var(--text)]">{user?.email ?? '—'}</dd>
            </div>
            <div className="flex justify-between border-b border-[var(--border)] pb-2">
              <dt className="text-[var(--text-muted)]">{t('settings.user_id')}</dt>
              <dd className="font-mono text-xs text-[var(--text)]">{user?.id ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-[var(--text-muted)]">{t('settings.current_tenant')}</dt>
              <dd className="text-[var(--text)]">
                {currentTenant?.name ?? '—'}{' '}
                <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)] ml-2">
                  {currentTenant?.role}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        {/* ========== Aparență ========== */}
        <div className="card">
          <h2 className="display-title-sm mb-3">{t('settings.appearance')}</h2>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-[var(--text-muted)] mb-1.5">
                {t('settings.language')}
              </div>
              <LanguagePicker />
            </div>
            <div>
              <div className="text-xs text-[var(--text-muted)] mb-1.5">
                {t('settings.appearance')}
              </div>
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* ========== Securitate ========== */}
        <div className="card">
          <h2 className="display-title-sm mb-3">{t('settings.security')}</h2>
          <div className="space-y-5">
            <TwoFactorSection />
            <div className="border-t border-[var(--border)] pt-5">
              <ChangePasswordSection />
            </div>
            <div className="border-t border-[var(--border)] pt-5">
              <RecoveryPhraseSection />
            </div>
            <div className="border-t border-[var(--border)] pt-5">
              <SessionsSection />
            </div>
          </div>
        </div>

        {/* ========== Membrii spațiului curent ========== */}
        {currentTenant && currentTenantId && (
          <div className="card">
            <h2 className="display-title-sm mb-3">
              {t('members.title', { name: currentTenant.name })}
            </h2>
            <MembersSection tenantId={currentTenantId} myRole={currentTenant.role} />
          </div>
        )}

        {/* ========== Invitații tenant (doar owner/admin) ========== */}
        {canInvite && currentTenantId && (
          <div className="card">
            <h2 className="display-title-sm mb-3">{t('settings.tenant_invites')}</h2>
            <InvitesSection tenantId={currentTenantId} />
          </div>
        )}

        {/* ========== Ștergerea spațiului curent (doar owner) ========== */}
        {currentTenant?.role === 'owner' && currentTenantId && (
          <div className="card">
            <h2 className="display-title-sm mb-3">{t('space_delete.section_title')}</h2>
            <DeleteSpaceSection tenantId={currentTenantId} tenantName={currentTenant.name} />
          </div>
        )}
      </div>
    </div>
  );
}
